import type {ViewConfigurationPayload} from '../../components/board/issues/config-payload'

/**
 * A saved view's applied configuration: the settings the view is actually rendered against.
 * When the saved-view page payload carries a controller-resolved envelope, the host feeds
 * that envelope's applied configuration straight through the provider.
 */
export type ViewConfiguration = ViewConfigurationPayload

/**
 * The dirty diff over a saved baseline: missing inherits the baseline; `null` explicitly
 * clears a nullable option. This is what the URL encodes, and what the server-side
 * controller validates when it resolves the applied configuration.
 */
export type ViewConfigurationOverrides = Partial<ViewConfiguration>

export function sameVisibleFields(left: readonly string[], right: readonly string[]): boolean {
  const leftSet = new Set(left)
  const rightSet = new Set(right)
  return leftSet.size === rightSet.size && [...leftSet].every(field => rightSet.has(field))
}

/**
 * Applies overrides to a baseline to produce an applied configuration. Transitional helper
 * for hosts that do not yet receive a controller-resolved envelope; once the payload
 * supplies applied configuration directly, hosts pass that through instead. Callers should
 * not assume the result matches what a request with these overrides would actually be
 * served: the server may validate the diff and fall back on an unsupported combination.
 */
export function resolveViewConfiguration(
  baseline: ViewConfiguration,
  overrides: ViewConfigurationOverrides,
): ViewConfiguration {
  return {
    layout: overrides.layout ?? baseline.layout,
    groupBy: overrides.groupBy === undefined ? baseline.groupBy : overrides.groupBy,
    swimlaneBy: overrides.swimlaneBy === undefined ? baseline.swimlaneBy : overrides.swimlaneBy,
    sorts: overrides.sorts ?? baseline.sorts,
    visibleFields: overrides.visibleFields ?? baseline.visibleFields,
  }
}

export function getConfigurationDirtyState(baseline: ViewConfiguration, configuration: ViewConfiguration) {
  return {
    layout: baseline.layout !== configuration.layout,
    groupBy: baseline.groupBy !== configuration.groupBy,
    swimlaneBy: baseline.swimlaneBy !== configuration.swimlaneBy,
    sorts:
      baseline.sorts.length !== configuration.sorts.length ||
      baseline.sorts.some((sort, index) => {
        const current = configuration.sorts[index]
        return sort.field !== current?.field || sort.direction !== current.direction
      }),
    visibleFields: !sameVisibleFields(baseline.visibleFields, configuration.visibleFields),
  }
}
